import { useEffect, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

type Platform = "ios" | "android";

interface PublicConfig {
  minNativeBuild?: Partial<Record<Platform, number>>;
}

async function isOutdated(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") return false;

  try {
    const [info, configRes] = await Promise.all([
      CapApp.getInfo(),
      fetch("/api/public/config", { credentials: "include" }),
    ]);
    if (!configRes.ok) return false;
    const config = (await configRes.json()) as PublicConfig;
    const min = config.minNativeBuild?.[platform];
    const current = parseInt(String(info.build), 10);
    if (typeof min !== "number" || !Number.isFinite(current)) return false;
    return current < min;
  } catch {
    return false;
  }
}

export function NativeVersionGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"checking" | "ok" | "outdated">("checking");

  useEffect(() => {
    let cancelled = false;
    void isOutdated().then((outdated) => {
      if (cancelled) return;
      setState(outdated ? "outdated" : "ok");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking" && Capacitor.isNativePlatform()) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (state === "outdated") return <UpdateRequired />;
  return <>{children}</>;
}

function UpdateRequired() {
  const isIos = Capacitor.getPlatform() === "ios";
  // Send people to the STORE. This used to open `${origin}/install`, which is
  // the server's install page — and on Android that page's whole job is to emit
  // an intent:// URL that opens app.marketlink.mobile. Someone who already has
  // the app therefore tapped "Update Now" and got handed back to the app they
  // were already stuck in, so the button looked dead and the only way out was
  // uninstalling. Capacitor fires an ACTION_VIEW intent for any host that isn't
  // the app's own, so both of these hand off to the store app.
  const storeScheme = isIos
    ? "itms-apps://apps.apple.com/us/app/id6762698222"
    : "market://details?id=app.marketlink.mobile";
  const storeWeb = isIos
    ? "https://apps.apple.com/us/app/stoop-fresh-local-food/id6762698222"
    : "https://play.google.com/store/apps/details?id=app.marketlink.mobile";

  const handleUpdate = async () => {
    try {
      window.location.href = storeScheme;
    } catch {
      await Browser.open({ url: storeWeb }).catch(() => {
        window.location.href = storeWeb;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background px-6 text-center">
      <h1 className="mb-3 text-2xl font-semibold text-foreground">Update required</h1>
      <p className="mb-8 max-w-sm text-muted-foreground">
        A newer version of Stoop is available. Please update to keep using the app.
      </p>
      <button
        onClick={handleUpdate}
        className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground"
      >
        Update Now
      </button>
      {/* A real link, so there is a second way out if the store app can't be
          reached — the previous screen had none, and the exit was a reinstall. */}
      <a href={storeWeb} className="mt-5 text-sm text-muted-foreground underline">
        Open the {isIos ? "App Store" : "Play Store"} in a browser
      </a>
    </div>
  );
}
